import { environment } from '@/environment.ts';
import { useHttpClientContext } from '@/shared/context/HttpClientContext.tsx';
import { SearchIcon } from '@schuettflix/planum-icons-react';
import { Button, TextField } from '@schuettflix/planum-react';
import { Combobox } from '@schuettflix/react-components';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useIndividualLineItemProductOptions } from '../hooks/useIndividualLineItemProductOptions.tsx';
import { useUploadFiles } from '../hooks/useUploadFiles.tsx';
import type { PartialIndividualLineItem } from '../shared/IndividualLineItemSchema.ts';
import type { ProductFallbackDataMap } from '../types/ProductFallbackDataMap.ts';

export type AutofillProps = {
    productFallbackDataMap: ProductFallbackDataMap;
    onAdd: (lineItems: PartialIndividualLineItem[]) => void;
};

const showAccompanyingDocumentProduct = environment.SFLX_ENVIRONMENT !== 'prod';

export function Autofill(props: AutofillProps) {
    const [needle, setNeedle] = useState('');

    const productNameOptions = useIndividualLineItemProductOptions(props.productFallbackDataMap);

    const [productIdWeighingNote, setProductIdWeighingNote] = useState<string | null>(
        productNameOptions[0]?.value ?? null
    );
    const [productIdAccompanyingDocument, setProductIdAccompanyingDocument] = useState<string | null>(
        productNameOptions[1]?.value ?? productNameOptions[0]?.value ?? null
    );

    const uploadFiles = useUploadFiles();
    const { t } = useTranslation();
    const { documentManagementBridgeClient } = useHttpClientContext();

    const search = documentManagementBridgeClient.query.useMutation('get', '/v1/weighing-notes/search/bundled', {
        async onSuccess(data) {
            let documentUrls: string[] | undefined = undefined;
            const document = await documentManagementBridgeClient.fetch.GET('/v1/documents/download/{id}', {
                params: { path: { id: data.documentId } },
                parseAs: 'blob',
            });

            if (document.data) {
                const file = new File([document.data], `${data.documentId}.pdf`, { type: 'application/pdf' });
                const uploaded = await uploadFiles([file], 'dms');
                if (uploaded) {
                    documentUrls = uploaded.map(d => d.remotePath);
                }
            }

            const maxLength = Math.max(data.weighingNotes.length, data.accompanyingDocuments.length);

            const lineItems: PartialIndividualLineItem[] = [];

            for (let i = 0; i < maxLength; i++) {
                if (i < data.weighingNotes.length) {
                    const item = data.weighingNotes[i];
                    lineItems.push({
                        amount: item.netWeight ?? undefined,
                        documentUrls: documentUrls,
                        licensePlate: item.licencePlate || undefined,
                        productId: productIdWeighingNote ?? undefined,
                        referenceDocument: item.deliveryWeighingNoteNumber,
                        serviceDate: item.createdDate ? new Date(item.createdDate) : undefined,
                        accompanyingDocumentNumber:
                            item.accompanyingDocumentNumber !== null ? item.accompanyingDocumentNumber : undefined,
                    });
                }

                if (i < data.accompanyingDocuments.length) {
                    const item = data.accompanyingDocuments[i];
                    lineItems.push({
                        amount: item.netWeight ?? 1,
                        documentUrls: documentUrls,
                        licensePlate: item.licencePlate || undefined,
                        productId: productIdAccompanyingDocument ?? undefined,
                        referenceDocument: item.accompanyingDocumentNumber,
                        serviceDate: item.createdDate ? new Date(item.createdDate) : undefined,
                        accompanyingDocumentNumber:
                            item.accompanyingDocumentNumber !== null ? item.accompanyingDocumentNumber : undefined,
                    });
                }
            }

            props.onAdd(lineItems);
        },
    });

    return (
        <div className="bg-light-gray-100 mt-2 flex flex-col gap-2 rounded p-2">
            <p className="font-copy-md">{t('fulfillment.autofill.title')}</p>
            <form
                className="flex items-center gap-4"
                onSubmit={e => {
                    e.preventDefault();
                    search.mutate({ params: { query: { documentId: Number.parseInt(needle) } } });
                    setNeedle('');
                }}
            >
                <TextField
                    size="lg"
                    className="min-h-12 min-w-[20rem]"
                    value={needle}
                    disabled={search.isPending}
                    onChange={e => setNeedle(e.target.value)}
                    placeholder={t('fulfillment.autofill.search.label')}
                />
                <Combobox
                    className="min-w-[12rem]"
                    options={productNameOptions}
                    disabled={search.isPending}
                    value={productIdWeighingNote}
                    onChange={setProductIdWeighingNote}
                    placeholder={t('fulfillment.autofill.productWeighingNote.placeholder')}
                    variant="sm"
                />
                {showAccompanyingDocumentProduct && (
                    <Combobox
                        className="min-w-[12rem]"
                        options={productNameOptions}
                        disabled={search.isPending}
                        value={productIdAccompanyingDocument}
                        onChange={setProductIdAccompanyingDocument}
                        placeholder={t('fulfillment.autofill.productAccompanyingDocument.placeholder')}
                        variant="sm"
                    />
                )}
                <Button type="submit" size="lg" loading={search.isPending}>
                    {t('fulfillment.autofill.submit.title')} <SearchIcon />
                </Button>
            </form>
        </div>
    );
}
